import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TextArea } from '@tcrd/ui';
import { TripNeeds } from '../../components/TripExtras';
import { useToast } from '../../components/toast';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, Phone, QrCode, Search, Users } from 'lucide-react';
import { formatMoney, RIDE_STATUS_LABEL } from '@tcrd/shared';
import { Avatar, Badge, Button, Card, cn, EmptyState, ErrorState, Spinner, Stat } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { api, errorMessage } from '../../lib/api';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { whatsappLink } from '../landing/usePublicProfile';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  photoUrl: string | null;
  linked: boolean;
  since: string;
  rides: number;
  spent: string;
  lastRide: { id: string; at: string; status: string; destination: string } | null;
}

interface Clients {
  summary: { total: number; linked: number; active30: number; newThisMonth: number };
  items: Client[];
}

interface ClientDetail extends Omit<Client, 'rides' | 'spent' | 'lastRide'> {
  email: string | null;
  note: string;
  rating: string;
  ratingCount: number;
  needs: string[];
  preferences: Record<string, string | boolean | number>;
  totals: { rides: number; spent: string; tips: string };
  rides: Array<{ id: string; code: string; at: string; status: string; origin: string; destination: string; fare: string; paymentStatus: string | null }>;
}

/** "Hoy", "ayer", "hace 3 días"… (o la fecha para viajes futuros o lejanos). */
export function relativeDay(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const days = Math.round((new Date(today.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000);
  if (days < 0) return `próximo: ${fmtDate(d)}`;
  if (days === 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  if (days < 30) return `hace ${Math.floor(days / 7)} semana${days >= 14 ? 's' : ''}`;
  return fmtDate(d);
}

const SORTS = [
  ['recent', 'Último viaje'],
  ['rides', 'Más viajes'],
  ['new', 'Nuevos'],
] as const;
type Sort = (typeof SORTS)[number][0];

function Contact({ phone, name }: { phone: string | null; name: string }) {
  if (!phone) return null;
  const wa = whatsappLink(phone, `Hola ${name}, te escribe tu conductor de TuConductorRD.`);
  return (
    <div className="flex gap-2 shrink-0">
      {wa && (
        <a href={wa} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="tap size-10 rounded-full bg-[var(--surface-2)] flex items-center justify-center" aria-label={`WhatsApp a ${name}`}>
          <MessageCircle className="size-5 text-[#128C7E]" />
        </a>
      )}
      <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()} className="tap size-10 rounded-full bg-[var(--surface-2)] flex items-center justify-center" aria-label={`Llamar a ${name}`}>
        <Phone className="size-5" />
      </a>
    </div>
  );
}

/** Conductor: sus clientes (vinculados con su enlace o que ya viajaron con él). */
export function ClientsPage() {
  const q = useQuery({ queryKey: ['driver-clients'], queryFn: () => api<Clients>('/drivers/me/clients') });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('recent');

  const items = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = (q.data?.items ?? []).filter((c) => words.every((w) => `${c.firstName} ${c.lastName}`.toLowerCase().includes(w)));
    if (sort === 'rides') return [...list].sort((a, b) => b.rides - a.rides);
    if (sort === 'new') return [...list].sort((a, b) => new Date(b.since).getTime() - new Date(a.since).getTime());
    return list;
  }, [q.data, search, sort]);

  const s = q.data?.summary;
  return (
    <Page title="Mis clientes" back="/driver/more" onRefresh={() => q.refetch()} actions={<Link to="/driver/invite" className="tap flex items-center justify-center px-2" aria-label="Mi enlace y QR"><QrCode className="size-6" /></Link>}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {s && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Clientes" value={s.total} hint={`${s.linked} con tu enlace`} />
            <Stat label="Activos 30 días" value={s.active30} />
            <Stat label="Nuevos este mes" value={s.newThisMonth} tone={s.newThisMonth ? 'accent' : 'default'} />
          </div>

          {s.total === 0 ? (
            <EmptyState
              icon={<Users className="size-6" />}
              title="Aún no tienes clientes"
              description="Comparte tu enlace o tu código QR: quien se registre con él será tu cliente y sus viajes te llegarán solo a ti."
              action={<Link to="/driver/invite"><Button icon={<QrCode className="size-5" />}>Compartir mi enlace</Button></Link>}
            />
          ) : (
            <>
              <label className="flex items-center gap-2 h-12 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-3 focus-within:border-[var(--text)]">
                <Search className="size-5 text-[var(--text-muted)] shrink-0" aria-hidden />
                <span className="sr-only">Buscar cliente</span>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente por nombre" className="flex-1 min-w-0 bg-transparent outline-none text-base" autoComplete="off" />
              </label>
              <div role="radiogroup" aria-label="Ordenar" className="flex gap-2">
                {SORTS.map(([k, l]) => (
                  <button key={k} role="radio" aria-checked={sort === k} onClick={() => setSort(k)} className={cn('tap h-9 px-3 rounded-full border text-sm font-medium', sort === k ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)] bg-[var(--surface)]')}>
                    {l}
                  </button>
                ))}
              </div>
              {items.length === 0 && <p className="text-sm text-[var(--text-muted)]">Ningún cliente coincide con "{search}".</p>}
              <Card className="divide-y divide-[var(--border)] overflow-hidden">
                {items.map((c) => (
                  <Link key={c.id} to={`/driver/clients/${c.id}`} className="tap flex items-center gap-3 px-4 py-3 hover:bg-black/[0.03] dark:hover:bg-white/5">
                    <Avatar src={c.photoUrl} firstName={c.firstName} lastName={c.lastName} size={48} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate flex items-center gap-2">
                        {c.firstName} {c.lastName}
                        {c.linked && <Badge tone="neutral">Vinculado</Badge>}
                      </p>
                      <p className="text-sm text-[var(--text-muted)] truncate">
                        {c.lastRide ? `Último viaje ${relativeDay(c.lastRide.at)} · ${c.lastRide.destination}` : `Sin viajes aún · desde ${fmtDate(c.since)}`}
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {c.rides} viaje{c.rides === 1 ? '' : 's'} · {formatMoney(c.spent)}
                      </p>
                    </div>
                    <Contact phone={c.phone} name={c.firstName} />
                  </Link>
                ))}
              </Card>
            </>
          )}
        </div>
      )}
    </Page>
  );
}

const PREF_LABEL: Record<string, string> = { temperature: 'Temperatura', music: 'Música', conversation: 'Conversación', seat: 'Asiento' };

/** Conductor: un cliente con sus datos de contacto, totales y viajes con él. */
export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['driver-client', id], queryFn: () => api<ClientDetail>(`/drivers/me/clients/${id}`) });
  const c = q.data;
  const [note, setNote] = useState('');
  useEffect(() => setNote(c?.note ?? ''), [c?.note]);
  const saveNote = async () => {
    try {
      await api(`/drivers/me/clients/${id}/note`, { method: 'PUT', body: { note } });
      await qc.invalidateQueries({ queryKey: ['driver-client', id] });
      toast('Nota guardada', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };
  const prefs = Object.entries(c?.preferences ?? {}).filter(([, v]) => v !== '' && v !== false && v !== null);
  return (
    <Page title={c ? `${c.firstName} ${c.lastName}` : 'Cliente'} back="/driver/clients" onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {c && (
        <div className="flex flex-col gap-4">
          <Card className="p-5 flex items-center gap-4">
            <Avatar src={c.photoUrl} firstName={c.firstName} lastName={c.lastName} size={64} />
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold truncate">{c.firstName} {c.lastName}</p>
              <p className="text-sm text-[var(--text-muted)]">{c.linked ? 'Vinculado con tu enlace' : 'Ha viajado contigo'} · desde {fmtDate(c.since)}</p>
              {c.phone && <p className="text-sm">{c.phone}</p>}
            </div>
            <Contact phone={c.phone} name={c.firstName} />
          </Card>
          <Card className="p-4 flex flex-col gap-3">
            <p className="text-sm">
              Puntuación como pasajero: <strong>{Number(c.rating) > 0 ? `★ ${Number(c.rating).toFixed(1)}` : 'sin calificar'}</strong>
              {c.ratingCount ? <span className="text-[var(--text-muted)]"> ({c.ratingCount} calificación{c.ratingCount === 1 ? '' : 'es'})</span> : null}
            </p>
            <TripNeeds needs={c.needs} />
            <TextArea label="Nota privada (solo la ves tú)" placeholder="Ej.: Le gusta el aire bajito, prefiere música suave" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
            <Button size="sm" variant="secondary" disabled={note === (c.note ?? '')} onClick={() => void saveNote()}>Guardar nota</Button>
          </Card>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Viajes" value={c.totals.rides} />
            <Stat label="Te ha pagado" value={formatMoney(c.totals.spent)} />
            <Stat label="Propinas" value={formatMoney(c.totals.tips)} />
          </div>
          {prefs.length > 0 && (
            <Card className="p-4 text-sm">
              <p className="font-semibold mb-2">Preferencias</p>
              <ul className="flex flex-col gap-1">
                {prefs.map(([k, v]) => <li key={k}><span className="text-[var(--text-muted)]">{PREF_LABEL[k] ?? k}:</span> {v === true ? 'Sí' : String(v)}</li>)}
              </ul>
            </Card>
          )}
          <section>
            <p className="font-semibold mb-2">Viajes contigo</p>
            {c.rides.length === 0 && <p className="text-sm text-[var(--text-muted)]">Todavía no ha hecho viajes contigo.</p>}
            <Card className="divide-y divide-[var(--border)] overflow-hidden">
              {c.rides.map((r) => (
                <Link key={r.id} to={`/driver/rides/${r.id}`} className="tap block px-4 py-3 hover:bg-black/[0.03] dark:hover:bg-white/5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium">{fmtDateTime(r.at)}</p>
                    <Badge tone={r.status === 'COMPLETED' ? 'success' : r.status === 'CANCELLED' ? 'danger' : 'neutral'}>{RIDE_STATUS_LABEL[r.status as keyof typeof RIDE_STATUS_LABEL] ?? r.status}</Badge>
                  </div>
                  <p className="text-sm text-[var(--text-muted)] truncate">{r.origin} → {r.destination}</p>
                  <p className="text-sm">{formatMoney(r.fare)}</p>
                </Link>
              ))}
            </Card>
          </section>
        </div>
      )}
    </Page>
  );
}
